import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "../i18n";
import { Trash2, X } from "lucide-react";
import PageShell from "../components/PageShell";
import ConfirmDialog from "../components/ConfirmDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import CampaignTable from "../components/campaign/CampaignTable";
import CampaignLog from "../components/campaign/CampaignLog";
import CampaignRunProgress from "../components/campaign/CampaignRunProgress";
import CampaignFormDialog from "../components/campaign/CampaignFormDialog";
import SegmentManagerDialog from "../components/campaign/SegmentManagerDialog";
import CreateMenu from "../components/campaign/CreateMenu";
import {
  useCampaigns,
  useTriggerTypes,
  useCampaignLog,
  useCampaignStats,
  useCampaignHoldout,
  useCampaignRunState,
  useReconcileCampaignRunState,
  useCreateCampaign,
  useUpdateCampaign,
  useDeleteCampaign,
  useRunCampaign,
  useStopCampaign,
  useTestCampaign,
  useSegmentDefinitions,
} from "../features/super/hooks/useCampaigns";
import toast from "react-hot-toast";

/**
 * Query parameter holding the campaign in focus.
 *
 * Selection used to live in component state, so a reload (or a shared link)
 * always landed on an empty dashboard with the operator's campaign closed.
 * The id belongs in the URL for the same reason the log offset does not: it is
 * what the page is currently about.
 */
const CAMPAIGN_PARAM = "campaign";

/** Run states in which the campaign is still sending. */
const ACTIVE_RUN_STATES = new Set(["running", "stopping"]);

export default function CampaignDashboard() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Data via tanstack
  const {
    data: campaigns = [],
    isError: campaignsFailed,
    error: campaignsError,
    refetch: refetchCampaigns,
  } = useCampaigns();
  const { data: triggerTypes = [] } = useTriggerTypes();
  const createMutation = useCreateCampaign();
  const updateMutation = useUpdateCampaign();
  const deleteMutation = useDeleteCampaign();
  const runMutation = useRunCampaign();
  const stopMutation = useStopCampaign();
  const testMutation = useTestCampaign();

  // UI state
  const selectedId = campaignIdFromParams(searchParams);
  const [busyId, setBusyId] = useState(null);
  const [logOffset, setLogOffset] = useState(0);

  // Dialogs. `editing` holds the campaign open in the form dialog; null with
  // `formOpen` means a new one is being created.
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [segmentsOpen, setSegmentsOpen] = useState(false);
  // Campaign the segment manager is scoped to: null lists and creates the
  // global definitions, an id lists and creates that campaign's own. The
  // header "Segments" button scopes it to the campaign in focus, the
  // Create-menu item leaves it global — without the id the operator would have
  // to open the manager for a campaign to reach the segments stored against
  // it, and `GET /campaigns/segments` without a `campaign_id` answers with
  // the `campaign_id IS NULL` rows alone.
  const [segmentsCampaignId, setSegmentsCampaignId] = useState(null);

  const openCreateForm = useCallback(() => {
    setEditingId(null);
    setFormOpen(true);
  }, []);

  const openEditForm = useCallback((id) => {
    setEditingId(id);
    setFormOpen(true);
  }, []);

  const openSegments = useCallback((campaignId) => {
    setSegmentsCampaignId(campaignId);
    setSegmentsOpen(true);
  }, []);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Test campaign dialog
  const [testTarget, setTestTarget] = useState(null);
  const [testPhone, setTestPhone] = useState("");

  const selectedCampaign = campaigns.find((c) => c.id === selectedId) || null;
  const editingCampaign = campaigns.find((c) => c.id === editingId) || null;

  const selectCampaign = useCallback(
    (id) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id == null) next.delete(CAMPAIGN_PARAM);
          else next.set(CAMPAIGN_PARAM, String(id));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // A finished run changes the numbers the stats/log panels show.
  const { data: logData, isError: logFailed } = useCampaignLog(
    selectedId,
    logOffset,
  );
  const { data: statsData } = useCampaignStats(selectedId);
  const { data: holdoutData } = useCampaignHoldout(selectedId);
  const { data: runState } = useCampaignRunState(selectedId);
  // The card reads the run from two sources that drift apart: the polled
  // query above, and the row's snapshot, taken at the last `campaigns`
  // refetch. The poll answers first, so the row only fills in the short
  // window before that — with its own state and the Stop that state carries,
  // which is right for a run already under way when the card opened. From
  // the first answer on the poll is the truth about the run, and
  // useReconcileCampaignRunState below re-reads it whenever the row still
  // claims a run that is over.
  const rowRun = selectedCampaign?.run;
  useReconcileCampaignRunState(
    selectedId,
    runState?.runState,
    rowRun?.runState,
  );
  const cardRun = runState ?? rowRun;
  // Delete waits on both sources, since they disagree for a while after a
  // run ends. It is a courtesy guard, not the rule: the endpoint answers 409
  // "Campaign is running — stop it before deleting" for a running campaign
  // whatever the client believes, and disabling the button keeps the operator
  // from bouncing off that error for a reason this screen already knows.
  const runActive =
    isRunActive(runState?.runState) || isRunActive(rowRun?.runState);
  const cardBusy = selectedCampaign != null && busyId === selectedCampaign.id;
  // Custom segment names for the `Имя сегмента` picker. Scoped to the campaign
  // the form is editing, not to the one the URL happens to point at — those
  // were the same campaign only while the operator had not navigated.
  const { data: segmentData } = useSegmentDefinitions(editingCampaign?.id);
  const customSegmentNames = (segmentData?.items ?? []).map((s) => s.name);

  // Selecting another campaign starts its log from the newest page.
  useEffect(() => {
    setLogOffset(0);
  }, [selectedId]);

  // Escape closes the opened campaign card. It stayed up after a run finished
  // and nothing on it said what dismissed it; the Close button is the visible
  // way out, this is the expected one. Dialogs handle their own Escape, so
  // the card does not also close under an open one.
  useEffect(() => {
    if (selectedId == null) return;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (
        formOpen ||
        segmentsOpen ||
        deleteTarget != null ||
        testTarget != null
      ) {
        return;
      }
      selectCampaign(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [
    selectedId,
    formOpen,
    segmentsOpen,
    deleteTarget,
    testTarget,
    selectCampaign,
  ]);

  const withBusy = async (id, action) => {
    setBusyId(id);
    try {
      await action();
    } finally {
      setBusyId((current) => (current === id ? null : current));
    }
  };

  const handleCreate = async (data) => {
    const created = await createMutation.mutateAsync(data);
    toast.success(t("campaign.toastCreated"));
    // Land the operator on what they just made instead of on a list they have
    // to find the new row in.
    selectCampaign(created.id);
  };

  const handleSave = async (id, data) => {
    await updateMutation.mutateAsync({ id, data });
    toast.success(t("campaign.toastUpdated"));
  };

  const handleToggle = (id, enabled) =>
    withBusy(id, async () => {
      const campaign = campaigns.find((c) => c.id === id);
      if (!campaign) return;
      try {
        await updateMutation.mutateAsync({
          id,
          data: { config: campaign.config, enabled, name: campaign.name },
        });
      } catch (err) {
        toast.error(describeError(err, t("campaign.toastToggleFailed")));
      }
    });

  const handleRun = (id) =>
    withBusy(id, async () => {
      try {
        await runMutation.mutateAsync(id);
        toast.success(t("campaign.toastRunStarted"));
      } catch (err) {
        toast.error(describeError(err, t("campaign.toastRunFailed")));
      }
    });

  const handleStop = (id) =>
    withBusy(id, async () => {
      try {
        await stopMutation.mutateAsync(id);
        toast.success(t("campaign.toastStopRequested"));
      } catch (err) {
        toast.error(describeError(err, t("campaign.toastStopFailed")));
      }
    });

  const handleTest = (id) => {
    setTestTarget(id);
    setTestPhone("");
  };

  const handleTestSend = async () => {
    if (!testTarget || !testPhone.trim()) return;
    try {
      const result = await testMutation.mutateAsync({
        id: testTarget,
        phone: testPhone.trim(),
      });
      if (result.delivered > 0 && result.delivered < result.attempted) {
        toast(describePartialDelivery(result, t), { icon: "⚠️" });
      } else if (result.delivered > 0) {
        toast.success(
          t("campaign.toastTestResult", { name: result.customerName }),
        );
      } else {
        // A "successful" test that reached no device is the case that used to
        // report success while nothing was delivered.
        toast.error(
          t("campaign.toastTestNotDelivered", { name: result.customerName }),
        );
      }
      setTestTarget(null);
      setTestPhone("");
    } catch (err) {
      toast.error(describeError(err, t("campaign.toastTestFailed")));
    }
  };

  const handleDelete = () => {
    const id = deleteTarget;
    setDeleteTarget(null);
    return withBusy(id, async () => {
      try {
        await deleteMutation.mutateAsync(id);
        if (selectedId === id) selectCampaign(null);
        toast.success(t("campaign.toastDeleted"));
      } catch (err) {
        toast.error(describeError(err, t("campaign.toastDeleteFailed")));
      }
    });
  };

  const getLabel = (triggerType) => {
    const found = triggerTypes.find((tt) => tt.triggerType === triggerType);
    return found?.label || triggerType;
  };

  const deleteTargetName =
    deleteTarget != null
      ? campaigns.find((c) => c.id === deleteTarget)?.name || `#${deleteTarget}`
      : "";

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <PageShell title={t("page.marketing")}>
      <div className="flex flex-col gap-4 h-full">
        {/* ── Campaign list ── */}
        <div className="w-full shrink-0">
          <div className="bg-c-gray-600 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-xl">
                {t("campaign.count", { count: campaigns.length })}
              </h2>
              <div className="flex items-center gap-2">
                {/* "Segments" reaches the definitions stored against the
                    campaign in focus — the ones this campaign's own picker
                    lists — while the Create menu's segment item opens the same
                    manager globally. Two entry points, two scopes, each one
                    stated by the control that opens it. */}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => openSegments(selectedId ?? null)}
                >
                  {t("campaign.segmentsBtn")}
                </Button>
              </div>
            </div>

            {campaignsFailed && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm flex items-center justify-between gap-3">
                <span>
                  {describeError(campaignsError, t("campaign.toastLoadFailed"))}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={refetchCampaigns}
                >
                  {t("common.retry")}
                </Button>
              </div>
            )}

            <CampaignTable
              campaigns={campaigns}
              triggerTypes={triggerTypes}
              selectedId={selectedId}
              busyId={busyId}
              onSelect={selectCampaign}
              onToggle={handleToggle}
              onRun={handleRun}
            />

            {/* Create sits BELOW the rows: in the header it was the first
                thing on the page and its menu opened down over the campaigns
                it was about. Here it reads as "add to this list". */}
            <div className="flex justify-end">
              <CreateMenu
                items={[
                  {
                    key: "campaign",
                    label: t("campaign.createTitle"),
                    description: t("campaign.createMenuCampaign"),
                    onSelect: openCreateForm,
                  },
                  {
                    key: "segment",
                    label: t("campaign.segmentMenuTitle"),
                    description: t("campaign.createMenuSegment"),
                    onSelect: () => openSegments(null),
                  },
                ]}
              />
            </div>
          </div>
        </div>

        {/* ── Selected campaign ── */}
        {selectedCampaign && (
          <div className="w-full shrink-0">
            <div className="bg-c-gray-600 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-xl truncate">
                  {selectedCampaign.name ||
                    t("campaign.campaignFallback", { id: selectedId })}
                  {" — "}
                  {getLabel(selectedCampaign.triggerType)}
                </h2>
                <div className="flex items-center gap-2 shrink-0">
                  {/* The actions that moved out of the list row live here,
                      with the card's Close: Edit, Test, Delete. Stop moved
                      too, into the run progress below — next to the progress
                      it halts — which is why that panel is the run's one
                      report and reconciles the polled state with the list
                      row. Launch for a one-shot campaign and the enable
                      toggle for a scheduled one deliberately stayed in the
                      row — the campaign list owns them. */}
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={cardBusy}
                    onClick={() => openEditForm(selectedCampaign.id)}
                  >
                    {t("campaign.editBtn")}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={cardBusy}
                    onClick={() => handleTest(selectedCampaign.id)}
                  >
                    {t("campaign.test")}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={cardBusy || runActive}
                    onClick={() => setDeleteTarget(selectedCampaign.id)}
                    // Icon-only: a fourth text button made the header run out
                    // of room on narrower screens. The accessible name carries
                    // the full wording.
                    aria-label={t("campaign.delete")}
                    title={t("campaign.delete")}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => selectCampaign(null)}
                    title={t("campaign.closeHint")}
                  >
                    <X size={16} aria-hidden="true" className="mr-1" />
                    {t("campaign.close")}
                  </Button>
                </div>
              </div>

              {cardRun && (
                <CampaignRunProgress run={cardRun} onStop={handleStop} />
              )}

              {statsData && (
                <div className="grid grid-cols-3 gap-4">
                  <div
                    className="bg-c-gray-500/50 rounded-lg p-3 text-center"
                    title={
                      statsData.candidates == null
                        ? t("campaign.statCandidatesUnknown")
                        : undefined
                    }
                  >
                    <div className="text-2xl font-bold">
                      {statsData.candidates ?? "—"}
                    </div>
                    <div className="text-c-gray-400 text-sm">
                      {t("campaign.statCandidates")}
                    </div>
                  </div>
                  <div className="bg-c-gray-500/50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-400">
                      {statsData.sent}
                    </div>
                    <div className="text-c-gray-400 text-sm">
                      {t("campaign.statSent")}
                    </div>
                  </div>
                  <div className="bg-c-gray-500/50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-red-400">
                      {statsData.failed}
                    </div>
                    <div className="text-c-gray-400 text-sm">
                      {t("campaign.statFailed")}
                    </div>
                  </div>
                </div>
              )}

              {/* Holdout is not a segment, so the group it removes is
                  invisible everywhere else. Listing it lets the operator
                  compare the held-out customers with the send log. */}
              {holdoutData && holdoutData.holdoutPct > 0 && (
                <div className="space-y-2">
                  <h3 className="text-lg">
                    {t("campaign.holdoutTitle", {
                      pct: holdoutData.holdoutPct,
                    })}
                  </h3>
                  <p className="text-c-gray-400 text-xs">
                    {t("campaign.holdoutHint")}
                  </p>
                  {holdoutData.total > holdoutData.rows.length && (
                    <p className="text-c-gray-400 text-xs">
                      {t("campaign.holdoutShown", {
                        shown: holdoutData.rows.length,
                        total: holdoutData.total,
                      })}
                    </p>
                  )}
                  {holdoutData.rows.length === 0 ? (
                    <p className="text-c-gray-400 text-sm">
                      {t("campaign.holdoutEmpty")}
                    </p>
                  ) : (
                    <div className="overflow-auto max-h-[200px]">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-c-gray-400/30">
                            <th className="text-left py-2 px-2 text-c-gray-400">
                              {t("campaign.colCustomer")}
                            </th>
                            <th className="text-left py-2 px-2 text-c-gray-400">
                              {t("campaign.colPhone")}
                            </th>
                            <th className="text-left py-2 px-2 text-c-gray-400">
                              {t("campaign.segmentPreviewBalance")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {holdoutData.rows.map((row) => (
                            <tr
                              key={row.customerId}
                              className="border-b border-c-gray-400/10"
                            >
                              <td className="py-2 px-2">
                                {[row.firstname, row.lastname]
                                  .filter(Boolean)
                                  .join(" ") || `#${row.customerId}`}
                              </td>
                              <td className="py-2 px-2 text-c-gray-400">
                                {row.phone || "—"}
                              </td>
                              <td className="py-2 px-2 text-c-gray-400">
                                {row.minutesBalance}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <h3 className="text-lg">{t("campaign.log")}</h3>
                {logFailed ? (
                  <div className="text-red-400 text-sm py-4 text-center">
                    {t("campaign.toastLogFailed")}
                  </div>
                ) : (
                  <CampaignLog
                    log={logData}
                    offset={logOffset}
                    onOffsetChange={setLogOffset}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Campaign create / edit ── */}
      <CampaignFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        campaign={editingCampaign}
        triggerTypes={triggerTypes}
        segmentOptions={customSegmentNames}
        saving={saving}
        onCreate={handleCreate}
        onUpdate={handleSave}
      />

      {/* ── Segments ── */}
      <SegmentManagerDialog
        open={segmentsOpen}
        onOpenChange={setSegmentsOpen}
        campaignId={segmentsCampaignId}
      />

      {/* ── Delete confirmation ── */}
      <ConfirmDialog
        open={deleteTarget != null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t("campaign.confirmDeleteTitle")}
        itemName={deleteTargetName}
        message={t("campaign.confirmDeleteMessage", { item: deleteTargetName })}
        confirmText={t("common.delete")}
        onConfirm={handleDelete}
      />

      {/* ── Test campaign dialog ── */}
      <ConfirmDialog
        open={testTarget != null}
        onOpenChange={(open) => {
          if (!open) {
            setTestTarget(null);
            setTestPhone("");
          }
        }}
        title={t("campaign.testDialogTitle")}
        message={t("campaign.testDialogDesc")}
        customContent={
          <div className="space-y-2">
            <Label>{t("campaign.testDialogPhone")}</Label>
            <Input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder={t("campaign.testDialogPhonePlaceholder")}
              disabled={testMutation.isPending}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !testMutation.isPending) {
                  handleTestSend();
                }
              }}
            />
          </div>
        }
        confirmText={t("campaign.testDialogSend")}
        cancelText={t("campaign.testDialogCancel")}
        // Primary: sending a test is the dialog's affirmative action, and it
        // read as a disabled/secondary action while "secondary" was set.
        confirmVariant="default"
        onConfirm={handleTestSend}
        disabled={testMutation.isPending}
      />
    </PageShell>
  );
}

/**
 * The campaign id in the URL, or null when the parameter is absent or junk.
 *
 * A hand-edited `?campaign=abc` must not select the campaign with a NaN id —
 * the detail panels would render empty headers for a campaign that does not
 * exist, and every poll below would fire against it.
 */
function campaignIdFromParams(searchParams) {
  const raw = searchParams.get(CAMPAIGN_PARAM);
  if (raw == null || raw === "") return null;
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Surface the backend's error message instead of a bare "request failed".
 *
 * `apiFetch` callers throw `await resp.text()`, so the useful part — "Campaign
 * has 12 unfulfilled bonus promises" — was being caught and discarded by a
 * bare `catch {}`.
 */
function describeError(err, fallback) {
  if (typeof err === "string" && err.trim()) return err;
  if (err?.message) return err.message;
  return fallback;
}

/**
 * Whether the given run state means the campaign is still sending.
 *
 * A run in `running` or `stopping` has recipients left to push to, so the
 * card's Delete stays disabled until it finishes. Nothing bad happens if the
 * operator forces it: the endpoint answers 409 "Campaign is running — stop
 * it before deleting" (backend `delete_campaign`), so the guard saves a
 * rejected round trip, not data. The card asks it about the polled state and
 * the list row alike, because the two disagree for a while after a run ends:
 * the row keeps saying "running" until something refetches the list.
 *
 * Launch and the enable toggle are not card controls; they sit in the row's
 * left cell and are guarded by the campaign list's own run state, computed
 * from `c.run?.runState` in CampaignTable.
 */
function isRunActive(runState) {
  return Boolean(runState && ACTIVE_RUN_STATES.has(runState));
}

function describePartialDelivery(result, t) {
  const message = t("campaign.toastTestPartial", {
    name: result.customerName,
    delivered: result.delivered,
    attempted: result.attempted,
    pruned: result.pruned ?? 0,
  });
  const errors = result.errors ?? [];
  return errors.length ? `${message} (${errors.join(", ")})` : message;
}
